import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpCurriculumGenerationPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', IconComponent: Icons.BookOpen },
    { id: 'creating-curricula', name: 'Creating Curricula', IconComponent: Icons.Plus },
    { id: 'assessment-frameworks', name: 'Assessment Frameworks', IconComponent: Icons.ClipboardDocumentList },
    { id: 'personalization', name: 'Personalization', IconComponent: Icons.Target },
    { id: 'managing-curricula', name: 'Managing Curricula', IconComponent: Icons.Save }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center"><Icons.BookOpen className="w-8 h-8 mr-3" /> Curriculum Generation Help</h1>
              <p className="text-gray-600 mt-2">
                Complete guide to creating personalized curricula for students with SEN
              </p>
            </div>
            <Link
              to="/generate-curriculum"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Generate Curriculum
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Curriculum Generation Overview</h2>
                <p className="text-gray-600 mb-4">
                  The Curriculum Generation system uses AI-powered technology to create personalized learning 
                  experiences for students with Special Educational Needs. It considers individual student 
                  requirements, learning styles, and assessment frameworks to generate appropriate curricula.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Robot className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">AI-Powered</h3>
                    <p className="text-sm text-gray-600 mt-2">Advanced algorithms for personalized learning</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Target className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Personalized</h3>
                    <p className="text-sm text-gray-600 mt-2">Tailored to individual student needs</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ClipboardDocumentList className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Framework-Based</h3>
                    <p className="text-sm text-gray-600 mt-2">Built on proven assessment frameworks</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'creating-curricula' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Creating Curricula</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Step-by-Step Process</h3>
                    <ol className="text-blue-800 space-y-2 ml-4">
                      <li>1. Select Students
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Choose one or multiple students</li>
                          <li>• Consider grouping students with similar needs</li>
                          <li>• Ensure students have complete profiles</li>
                        </ul>
                      </li>
                      <li>2. Choose Assessment Framework
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Select appropriate framework (Engagement Model, etc.)</li>
                          <li>• Consider student's age and attainment level</li>
                          <li>• Review framework requirements</li>
                        </ul>
                      </li>
                      <li>3. Set Parameters
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Specify age and attainment level</li>
                          <li>• Select communication methods</li>
                          <li>• Identify areas of need</li>
                        </ul>
                      </li>
                      <li>4. Generate and Review
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Generate the curriculum</li>
                          <li>• Review content and objectives</li>
                          <li>• Make adjustments if needed</li>
                        </ul>
                      </li>
                      <li>5. Save and Implement
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Save to your system</li>
                          <li>• Share with relevant staff</li>
                          <li>• Begin implementation</li>
                        </ul>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Important Considerations</h3>
                    <ul className="text-green-800 space-y-1">
                      <li>• Ensure student profiles are complete and up-to-date</li>
                      <li>• Consider individual learning styles and preferences</li>
                      <li>• Review generated content for appropriateness</li>
                      <li>• Adapt activities to available resources</li>
                      <li>• Plan for regular review and adjustment</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'assessment-frameworks' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Assessment Frameworks</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Engagement Model</h3>
                    <p className="text-gray-600 mb-2">
                      Designed for students working below the level of the National Curriculum, focusing on 
                      engagement, responsiveness, and early learning skills.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Suitable for students with profound learning difficulties</li>
                      <li>• Focuses on sensory engagement and communication</li>
                      <li>• Emphasizes personal development and well-being</li>
                      <li>• Includes physical development and creative arts</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.BookOpen className="w-5 h-5 mr-2" /> Pre-Key Stage Standards</h3>
                    <p className="text-gray-600 mb-2">
                      For students working below the expected standard at the end of key stage 1, 
                      providing a foundation for future learning.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Covers English, mathematics, and science</li>
                      <li>• Focuses on basic skills and concepts</li>
                      <li>• Prepares students for key stage 1</li>
                      <li>• Includes practical and hands-on activities</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> P-Scales</h3>
                    <p className="text-gray-600 mb-2">
                      Performance scales for students with special educational needs working below 
                      level 1 of the National Curriculum.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Eight levels (P1-P8) for each subject</li>
                      <li>• Covers English, mathematics, and science</li>
                      <li>• Provides detailed progression indicators</li>
                      <li>• Useful for tracking small steps of progress</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">Choosing the Right Framework</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Consider the student's current attainment level</li>
                      <li>• Review previous assessments and progress</li>
                      <li>• Consult with other professionals if needed</li>
                      <li>• Be prepared to adjust as students progress</li>
                      <li>• Use multiple frameworks if appropriate</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'personalization' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Personalization Features</h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.Users className="w-5 h-5 mr-2" /> Student-Specific</h3>
                      <ul className="text-blue-800 space-y-1">
                        <li>• Individual learning objectives</li>
                        <li>• Personalized activity suggestions</li>
                        <li>• Adapted difficulty levels</li>
                        <li>• Student interest integration</li>
                      </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Learning Areas</h3>
                      <ul className="text-green-800 space-y-1">
                        <li>• Communication and language</li>
                        <li>• Physical development</li>
                        <li>• Personal, social, and emotional</li>
                        <li>• Literacy and numeracy</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.Wrench className="w-5 h-5 mr-2" /> Customization Options</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-purple-900 mb-2">Content Adaptation</h4>
                        <ul className="text-purple-800 text-sm space-y-1">
                          <li>• Adjust activity complexity</li>
                          <li>• Modify learning objectives</li>
                          <li>• Personalize examples and contexts</li>
                          <li>• Adapt assessment methods</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-purple-900 mb-2">Delivery Methods</h4>
                        <ul className="text-purple-800 text-sm space-y-1">
                          <li>• Visual, auditory, or kinesthetic</li>
                          <li>• Individual or group activities</li>
                          <li>• Technology integration</li>
                          <li>• Outdoor or indoor learning</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Progress Monitoring</h3>
                    <ul className="text-orange-800 space-y-1">
                      <li>• Regular assessment of objectives</li>
                      <li>• Adjustment based on progress</li>
                      <li>• Celebration of achievements</li>
                      <li>• Planning for next steps</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'managing-curricula' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Curricula</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Save className="w-5 h-5 mr-2" /> Saving and Storage</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Save curricula to your system for future reference</li>
                      <li>• Organize by student, subject, or date</li>
                      <li>• Create backups of important curricula</li>
                      <li>• Share with relevant staff members</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.PencilSquare className="w-5 h-5 mr-2" /> Editing and Updates</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Modify curricula based on student progress</li>
                      <li>• Update learning objectives as needed</li>
                      <li>• Adjust activities for better engagement</li>
                      <li>• Incorporate feedback from staff and students</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ArrowPath className="w-5 h-5 mr-2" /> Implementation</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Plan implementation timeline</li>
                      <li>• Prepare necessary resources</li>
                      <li>• Train staff on new curricula</li>
                      <li>• Monitor implementation effectiveness</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Evaluation</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Regular review of curriculum effectiveness</li>
                      <li>• Collect feedback from staff and students</li>
                      <li>• Assess student progress and engagement</li>
                      <li>• Plan improvements and modifications</li>
                    </ul>
                  </div>

                  <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Pro Tips:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Keep a library of successful curricula for reference</li>
                      <li>• Regular review and update of saved curricula</li>
                      <li>• Collaborate with other staff on curriculum development</li>
                      <li>• Document what works well for different students</li>
                      <li>• Plan for regular curriculum review meetings</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpCurriculumGenerationPage; 